import { Role, User } from '@/types/user.type'

// ----------------------------------------------------------------------

// Permission constants สำหรับแต่ละฟีเจอร์ตาม PRD
export const PERMISSIONS = {
  // Products module - SHOP only
  PRODUCTS: {
    VIEW: [Role.SHOP],
    CREATE: [Role.SHOP],
    EDIT: [Role.SHOP],
    DELETE: [Role.SHOP],
  },

  // Orders module - SHOP, ADMIN
  ORDERS: {
    VIEW: [Role.SHOP, Role.ADMIN],
    EDIT: [Role.SHOP, Role.ADMIN],
  },

  // Feed module - ADMIN only
  FEED: {
    VIEW: [Role.ADMIN],
  },

  // News module - ADMIN only
  NEWS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Shops management - ADMIN only
  SHOPS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Programs management - ADMIN only
  PROGRAMS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Banners management - ADMIN only
  BANNERS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Settings management - ADMIN only
  SETTINGS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Settings management - ADMIN only
  BANK_PAYMENT: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Privilege management - ADMIN only
  PRIVILEGES: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Redeem condition management - ADMIN only
  REDEEM_CONDITION: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Order privilege management - ADMIN only
  ORDER_PRIVILEGES: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Rooster match management - ADMIN only
  ROOSTER_MATCH: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Arena management - ADMIN only
  ARENA: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Transfer token management - ADMIN only
  TRANSFER_TOKEN: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Trend Sound management - ADMIN only
  TREND_SOUNDS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Trend management - ADMIN only
  TRENDS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },

  // Clips management - ADMIN only
  CLIPS: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },
  // Clip categories - ADMIN only
  CLIP_CATEGORIES: {
    VIEW: [Role.ADMIN],
    CREATE: [Role.ADMIN],
    EDIT: [Role.ADMIN],
    DELETE: [Role.ADMIN],
  },
} as const

// Helper functions สำหรับตรวจสอบ permissions
export const hasPermission = (
  user: User | null,
  allowedRoles: readonly Role[],
): boolean => {
  if (!user || !user.role) return false
  return allowedRoles.includes(user.role)
}

export const canViewProducts = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PRODUCTS.VIEW)

export const canManageProducts = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PRODUCTS.CREATE)

export const canViewOrders = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ORDERS.VIEW)

export const canManageOrders = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ORDERS.EDIT)

export const canViewFeed = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.FEED.VIEW)

export const canViewNews = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.NEWS.VIEW)

export const canManageNews = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.NEWS.CREATE)

export const canViewShops = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.SHOPS.VIEW)

export const canManageShops = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.SHOPS.CREATE)

export const canViewPrograms = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PROGRAMS.VIEW)

export const canManagePrograms = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PROGRAMS.CREATE)

export const canViewBanners = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.BANNERS.VIEW)

export const canViewSettings = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.SETTINGS.VIEW)

export const canViewBankPayment = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.BANK_PAYMENT.VIEW)

export const canManageBankPayment = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.BANK_PAYMENT.CREATE)

export const canViewPrivilege = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PRIVILEGES.VIEW)

export const canManagePrivilege = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PRIVILEGES.CREATE)

export const canViewOrderPrivilege = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.PRIVILEGES.VIEW)

export const canViewRedeemCondition = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.REDEEM_CONDITION.VIEW)

export const canManageRedeemCondition = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.REDEEM_CONDITION.CREATE)

export const canViewRoosterMatch = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ROOSTER_MATCH.VIEW)

export const canManageRoosterMatch = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ROOSTER_MATCH.CREATE)

export const canViewArena = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ARENA.VIEW)

export const canManageArena = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.ARENA.CREATE)
export const canViewTransferToken = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TRANSFER_TOKEN.VIEW)

export const canManageTransferToken = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TRANSFER_TOKEN.CREATE)

export const canViewTrendSounds = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TREND_SOUNDS.VIEW)

export const canManageTrendSounds = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TREND_SOUNDS.CREATE)

export const canViewTrends = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TRENDS.VIEW)

export const canManageTrends = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.TRENDS.CREATE)

export const canViewClips = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.CLIPS.VIEW)

export const canManageClips = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.CLIPS.CREATE)

export const canViewClipCategories = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.CLIP_CATEGORIES.VIEW)

export const canManageClipCategories = (user: User | null): boolean =>
  hasPermission(user, PERMISSIONS.CLIP_CATEGORIES.CREATE)

// ตรวจสอบว่าเป็น ADMIN หรือไม่
export const isAdmin = (user: User | null): boolean => user?.role === Role.ADMIN

// ตรวจสอบว่าเป็น SHOP หรือไม่
export const isShop = (user: User | null): boolean => user?.role === Role.SHOP

// ดึงรายการเมนูที่ user สามารถเข้าถึงได้ แบ่งเป็นกลุ่ม
export const getAvailableMenus = (user: User | null) => {
  const menuGroups = [
    {
      label: 'หลัก',
      items: [
        {
          title: 'แดชบอร์ด',
          path: '/dashboard',
          icon: 'LayoutDashboard',
          canAccess: true,
        },
      ],
    },
    {
      label: 'การขาย',
      items: [
        {
          title: 'รายการสินค้า',
          path: '/products',
          icon: 'Package',
          canAccess: canViewProducts(user),
        },
        {
          title: 'ออเดอร์',
          path: '/orders',
          icon: 'ShoppingCart',
          canAccess: canViewOrders(user),
        },
        {
          title: 'จัดการบัญชี',
          path: '/bank-payment',
          icon: 'BanknoteArrowDownIcon',
          canAccess: canViewBankPayment(user),
        },
      ],
    },
    {
      label: 'เนื้อหา',
      items: [
        {
          title: 'ฟีด',
          path: '/feeds',
          icon: 'Rss',
          canAccess: canViewFeed(user),
        },
        {
          title: 'RFC Talk',
          path: '/articles',
          icon: 'FileText',
          canAccess: canViewNews(user),
        },
        {
          title: 'โปรแกรม',
          path: '/programs',
          icon: 'Calendar',
          canAccess: canViewPrograms(user),
        },
        {
          type: 'divider',
          canAccess: canViewPrograms(user),
        },
        {
          title: 'เสียงเทรนด์',
          path: '/trend-sounds',
          icon: 'Music',
          canAccess: canViewTrendSounds(user),
        },
        {
          title: 'เทรนด์',
          path: '/trends',
          icon: 'Video',
          canAccess: canViewTrends(user),
        },
        {
          type: 'divider',
          canAccess: canViewTrendSounds(user) || canViewTrends(user),
        },
        {
          title: 'หมวดหมู่คลิป',
          path: '/clip-categories',
          icon: 'Layers',
          canAccess: canManageClipCategories(user),
        },
        {
          title: 'คลิป',
          path: '/clips',
          icon: 'Video',
          canAccess: canManageClips(user),
        },
      ],
    },
    {
      label: 'การแข่งขัน',
      items: [
        {
          title: 'จัดการสนาม',
          path: '/arenas',
          icon: 'MapPin',
          canAccess: canViewArena(user),
        },
        {
          title: 'จับคู่แข่งขัน',
          path: '/rooster-match',
          icon: 'Swords',
          canAccess: canViewRoosterMatch(user),
        },
      ],
    },
    {
      label: 'รางวัล',
      items: [
        {
          title: 'ของรางวัล',
          path: '/privileges',
          icon: 'Gift',
          canAccess: canViewPrivilege(user),
        },
        {
          title: 'จัดส่งรางวัล',
          path: '/order-privileges',
          icon: 'Package',
          canAccess: canViewOrderPrivilege(user),
        },
      ],
    },
    {
      label: 'การจัดการ',
      items: [
        {
          title: 'ร้านค้า',
          path: '/shops',
          icon: 'Store',
          canAccess: canViewShops(user),
        },
        {
          title: 'แบนเนอร์',
          path: '/banners',
          icon: 'ViewIcon',
          canAccess: canViewBanners(user),
        },
        {
          title: 'ยืนยันผู้ใช้งาน',
          path: '/user-verify',
          icon: 'VerifiedIcon',
          canAccess: isAdmin(user),
        },
        {
          title: 'ตั้งค่าระบบ',
          path: '/settings',
          icon: 'Settings',
          canAccess: canViewSettings(user),
        },
        {
          title: 'โอนเหรียญ',
          path: '/transfer-token',
          icon: 'ArrowLeftRight',
          canAccess: canViewTransferToken(user),
        },
      ],
    },
  ]

  // กรองเฉพาะกลุ่มที่มีเมนูที่เข้าถึงได้
  return menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.canAccess),
    }))
    .filter((group) => group.items.length > 0)
}
